//New message page

import React, { useState, useEffect, useRef } from "react";
import { Auth } from "aws-amplify";
import ApiService from "../ApiService";
import { Form, Button, Card, InputGroup, Modal, Row, Col, Dropdown, DropdownButton, Spinner } from "react-bootstrap";
import MuiAlert from "@material-ui/lab/Alert";
import Snackbar from "@material-ui/core/Snackbar";
import { ReactSearchAutocomplete } from "react-search-autocomplete";
import statesList from "../util/statesList";
import { parseAxiosError } from "../util/parserHelper";
import { clinicianIdFromRefreshAuth } from "../util/authUtil";
import { formatFileSize } from "../util/common";
import _ from "lodash";
import { AsyncTypeahead } from "react-bootstrap-typeahead";
import { AttachFile } from "@material-ui/icons";

function Alert(props) {
  return <MuiAlert elevation={6} variant="filled" {...props} />;
}

const searchByOptions = {
  patient_email: {
    slug: "patient_email",
    display: "Email"
  },
  patient_dob: {
    slug: "patient_dob",
    display: "DOB(yyyy-mm-dd) e.g: 1991-12-30"
  },
  patient_name: {
    display: "Name",
    slug: "patient_name"
  }
};

const SendMessage = ({ pId }) => {
  const [clinicianId, setClinicianId] = useState(null);
  const [kitId, setKitId] = useState("");
  const [toPatient, setToPatient] = useState("");
  const [messageText, setMessageText] = useState("");
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackText, setSnackText] = useState("");
  const [snackSeverity, setSnackSeverity] = useState("");
  const [fileType, setfileType] = useState("");
  const [file, setFile] = useState("");
  const [show, setShow] = useState(false);
  const [attachment, setAttachment] = useState([]);
  const [toValue, setToValue] = useState("");
  const [searchResults, setSearchResults] = useState();
  const [selectedSearchBy, setSelectedSearchBy] = useState("patient_email");
  const [patientId, setPatientId] = useState(pId || null);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [confirmModal, setConfirmModal] = useState(false);
  const autocompleteRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [msgLoading, setMsgLoading] = useState(false);
  const [verification, setVerification] = useState(false);
  const [selectedAttachment, setSelectedAttachment] = useState([]);
  const [subject, setSubject] = useState("");
  const [attachedFileS3URLs, setAttachedFileS3URLs] = useState([]);

  useEffect(() => {
    if (patientId) {
      ApiService.getPatientDetailsBySubId(patientId)
        .then(res => {
          console.log(res);
          if (res?.data) setSelectedPatient(res.data);
        })
        .catch(err => {
          console.log(err);
        });
    }
  }, [patientId]);

  const fileInputRef = useRef(null);

  const handleAttachmentClick = () => {
    if (selectedAttachment.length >= 5) {
      return handleSnackOpen("Only 5 File allowed at a time", "error");
    }
    fileInputRef.current.click();
  };

  const handleFileChange = async event => {
    const files = Array.from(event.target.files)[0];
    if (!files) {
      return;
    }
    handleSnackOpen("Uploading File", "info");
    try {
      await ApiService.uploadFile(files, "MESSAGE_ATTACHMENTS").then(resp => {
        const { url } = resp?.data?.body || {};
        files.url = url;
        setSelectedAttachment([...selectedAttachment, files]);
        setAttachedFileS3URLs([...attachedFileS3URLs, url]);
        handleSnackOpen("Upload success", "success");
      });
    } catch (err) {
      if (fileInputRef.current.value) {
        fileInputRef.current.value = null;
      }
      handleSnackOpen(`Upload failed, cause: ${parseAxiosError(err)?.message ?? "unknown"}`, "error");
    }
  };

  const debounceTimeout = useRef(null);

  const handleShow = () => setShow(true);

  useEffect(() => {
    const cliId = clinicianIdFromRefreshAuth();
    setClinicianId(cliId);
  }, []);

  const handleSnackOpen = (text, severity) => {
    setSnackSeverity(severity);
    setSnackText(text);
    setSnackOpen(true);
  };

  const handleSnackClose = () => {
    setSnackOpen(false);
  };

  const handleClose = () => {
    setShow(false);
    setConfirmModal(false);
    setVerification(false);
  };

  const changeHandler = event => {
    setFile(event.target.files[0]);
  };

  const submitFile = () => {
    attachment.push(file);
    setShow(false);
  };

  const sendMessageAPI = async () => {
    if (!subject.trim()) {
      handleSnackOpen("Subject cannot be empty", "error");
      return;
    }

    if (!messageText.trim()) {
      handleSnackOpen("Message cannot be empty", "error");
      return;
    }

    if (!selectedPatient) {
      handleSnackOpen("Please select a patient from list", "error");
      return;
    }

    handleSnackOpen("Sending Message...", "info");
    try {
      setMsgLoading(true);
      const msg = {
        patientId: patientId,
        base64Message: btoa(encodeURIComponent(messageText)),
        rawMessage: messageText,
        attachmentUrls: attachedFileS3URLs
      };
      const postPatientMessage = await ApiService.sendSecureMsgToPatient(msg)
        .then(res => {
          setPatientId("");
          setSelectedPatient(null);
          setVerification(false);
          setMessageText("");
          autocompleteRef?.current?.clear();
          setSelectedSearchBy("patient_email");
          setSearchResults(null);
          handleSnackOpen("Message Sent!", "success");
          handleClose();
          setSelectedAttachment([]);
          setAttachedFileS3URLs([]);
          if (fileInputRef.current.value) {
            fileInputRef.current.value = null;
          }
        })
        .catch(error => {
          const msg = error?.response?.data?.message ?? error?.message;
          handleSnackOpen(msg, "error");
        });
    } catch (err) {
      const msg = err?.response?.data?.message ?? err?.message;
      handleSnackOpen(`Failed to send message. cause: ${msg}`, "error");
    } finally {
      setMsgLoading(false);
    }
  };

  const handleOnSearch = async string => {
    //` Return if searchStr is empty
    if (!selectedSearchBy) {
      handleSnackOpen("Patient search failed. Select an option before typing", "error");
      return;
    }
    // if (!string || string.length < 3) {
    //   handleSnackOpen('Enter at-least 3 characters', 'warning')
    //   return
    // }
    //` Debounced Call
    try {
      setLoading(true);
      const response = await ApiService.searchPatient({
        type: selectedSearchBy,
        term: string?.toLowerCase()
      });
      setSearchResults(response?.data);
    } catch (error) {
      handleSnackOpen(`Patient search failed.${parseAxiosError(error)?.message}`, "error");
    } finally {
      setLoading(false);
    }
  };

  const handlePatientSelect = item => {
    setSelectedPatient(item[0]);
    setPatientId(item[0]?.id);
  };

  const handleOnClear = () => {
    setSearchResults();
  };

  const resetEverything = () => {
    setClinicianId(null);
    setKitId("");
    setToPatient("");
    setMessageText("");
    setSnackOpen(false);
    setSnackText("");
    setSnackSeverity("");
    setfileType("");
    setFile("");
    setShow(false);
    setAttachment([]);
    setToValue("");
    setSearchResults();
    setSelectedSearchBy("patient_email");
    setPatientId(null);
    setSelectedPatient(null);
    setConfirmModal(false);
    setVerification(false);
    autocompleteRef?.current?.clear();
    removeAllFiles();
  };

  const formatResult = item => {
    return (
      <div className="searchResult">
        <b className="searchResultName">
          {item?.firstName} {item?.lastName} - DOB: {item?.dob} - State: {item?.state}
        </b>
        <div className="searchResultEmail">{item?.email}</div>
        <hr style={{ height: 1, padding: 2, margin: 2 }} />
      </div>
    );
  };

  const removeAllFiles = () => {
    if (attachedFileS3URLs?.length > 0) {
      handleSnackOpen("Deleting Files", "info");
      attachedFileS3URLs.map((item, index) => {
        removeFileFromAttachment(index, true, index === attachedFileS3URLs.length - 1);
      });
    }
  };

  const removeFileFromAttachment = async (index, isFromRemoveAllFiles, isLastFile = false) => {
    const fileName = selectedAttachment[index]?.url;
    await ApiService.deleteFile("MESSAGE_ATTACHMENTS", fileName)
      .then(() => {
        if (!isFromRemoveAllFiles) {
          const newAttachment = [...selectedAttachment];
          newAttachment.splice(index, 1);
          setSelectedAttachment(newAttachment);

          const s3Urls = [...attachedFileS3URLs];
          const indexOfFileName = s3Urls.indexOf(fileName);
          s3Urls.splice(indexOfFileName, 1);
          setAttachedFileS3URLs(s3Urls);
          handleSnackOpen("Success", "success");
          if (fileInputRef.current.value) {
            fileInputRef.current.value = null;
          }
        } else if (isFromRemoveAllFiles && isLastFile) {
          setSelectedAttachment([]);
          setAttachedFileS3URLs([]);
          handleSnackOpen("Success", "success");
          if (fileInputRef.current.value) {
            fileInputRef.current.value = null;
          }
        }
      })
      .catch(error => {
        handleSnackOpen(parseAxiosError(error)?.reason, "error");
      });
  };

  return (
    <Card body className="CardFullBody">
      <Card.Body className="replyDetailBody">
        <Form id="msgSendForm">
          <Row className="pl-3 pr-3" style={{ width: "100%", display: "flex", justifyContent: "space-between" }} id="searchBox">
            <AsyncTypeahead
              id="searchBox-email"
              delay={200}
              ref={autocompleteRef}
              filterBy={["email", "dob", "firstName", "lastName"]}
              isLoading={loading}
              labelKey={selectedSearchBy === "patient_email" ? "email" : selectedSearchBy === "patient_dob" ? "dob" : "firstName"}
              // minLength={3}
              onSearch={handleOnSearch}
              options={searchResults || []}
              searchText="Searching..."
              placeholder={
                selectedSearchBy === "patient_email"
                  ? "Type an Email to search"
                  : selectedSearchBy === "patient_dob"
                  ? "Search by DOB"
                  : "Type Patient Name"
              }
              style={{ width: "55%" }}
              onChange={handlePatientSelect}
              renderMenuItemChildren={formatResult}
              onClear={handleOnClear}
              onInputChange={handleOnSearch}
              onPaste={handleOnSearch}
              useCache={false}
              selected={selectedPatient ? [selectedPatient] : []}
            />
            <Form.Group className="pl-2" as={Row} controlId="formLocation">
              <Form.Label id="cardFormLabel" column sm={4}>
                Search by
              </Form.Label>
              <Col sm={8}>
                <Form.Select
                  value={selectedSearchBy}
                  required
                  onChange={e => {
                    setSearchResults(null);
                    autocompleteRef?.current?.clear();
                    setSelectedSearchBy(e.target.value);
                  }}
                  name="searchBy"
                  id="formSelect"
                >
                  <option disabled selected value="">
                    Search patient by
                  </option>

                  {Object.entries(searchByOptions)?.map(([key, val]) => (
                    <option value={val.slug}>{val.display}</option>
                  ))}
                </Form.Select>
              </Col>
            </Form.Group>
          </Row>

          {/* add subject  */}
          <InputGroup className="mb-3 mt-3">
            <Form.Control
              //   id="borderless"
              placeholder="Subject"
              as="textarea"
              rows={1}
              value={subject}
              onChange={e => setSubject(e.target.value)}
            />
          </InputGroup>

          {/* <InputGroup className="mb-3">
                        <InputGroup.Text id="basic-addon1">Kit ID</InputGroup.Text>
                        <Form.Control id="borderless" aria-describedby="basic-addon1" type="text" placeholder="Enter Kit ID" value={kitId} onChange={(e) => setKitId(e.target.value)}/>
                    </InputGroup> */}
          {/* Display Name and Email  */}

          <InputGroup className="mb-3 mt-3">
            <Form.Control
              // id="borderless"
              placeholder="Enter your message here"
              as="textarea"
              rows={8}
              value={messageText}
              onChange={e => setMessageText(e.target.value)}
            />
          </InputGroup>
          <hr></hr>
          <div>
            <div className="attachment">
              {selectedAttachment?.map((item, index) => (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "fit-content",
                    padding: "8px",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    background: "#f5f5f5",
                    color: "#333",
                    fontWeight: "bold",
                    boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.1)",
                    margin: "10px 0px"
                  }}
                >
                  <div
                    style={{
                      color: "#b29ff1"
                    }}
                  >
                    {item.name}
                  </div>
                  <div style={{ margin: "0px 10px" }}>{formatFileSize(item.size)}</div>

                  <div
                    className="attachmentItemRemove"
                    onClick={() => {
                      removeFileFromAttachment(index, false);
                    }}
                  >
                    <i
                      class="fa fa-times"
                      aria-hidden="true"
                      style={{
                        margin: "0px 10px",
                        cursor: "pointer"
                      }}
                    ></i>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <Card.Footer as={Row} style={{ display: "flex", justifyContent: "space-between" }} className="cardFoot">
            {!loading && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center"
                }}
              >
                <Button disabled={!selectedPatient} id="msg_reply" class="replyButton sendButton text-right" onClick={() => setConfirmModal(true)}>
                  Send <i class="fa fa-paper-plane" aria-hidden="true"></i>
                </Button>
                <div>
                  <div
                    onClick={handleAttachmentClick}
                    style={{
                      cursor: "pointer"
                    }}
                  >
                    <AttachFile style={{ color: "black", marginLeft: "10px" }} />
                  </div>
                  <input type="file" ref={fileInputRef} style={{ display: "none" }} onChange={handleFileChange} multiple accept=".pdf,.jpg,.jpeg,.png" />
                </div>
              </div>
            )}
            {!loading && (
              <Button variant="dark" id="clear_form" class="text-right ml-3" onClick={resetEverything}>
                Reset
              </Button>
            )}
            {/* <OverlayTrigger
                            placement="bottom"
                            overlay={
                            <Tooltip>
                                Attachment
                            </Tooltip>
                            }
                        >
                            <Button onClick={handleShow} id="attachButton"><GrAttachment/></Button>
                        </OverlayTrigger>
                        {attachment.map((index) => (
                            <div className="fileAttachments" key={index.name}> {index.name.toString().substring(0, 20)}... <GrFormClose /> </div>
                        ))} */}
          </Card.Footer>
        </Form>
      </Card.Body>
      <Modal show={show} onHide={handleClose}>
        <Modal.Header>
          <Modal.Title>File attachment</Modal.Title>
        </Modal.Header>
        <Modal.Body id="modalBody">
          <Row>
            <Col md={3}>
              <p className="formTitle">File type</p>
            </Col>
            <Col>
              <Form.Select id="formSelect" aria-label="Default select example">
                <option>Select file type</option>
                <option value="1">Test Result</option>
                <option value="2">Lab Report</option>
              </Form.Select>
            </Col>
          </Row>
          <Row className="mt-3">
            <Col md={3}>
              <p className="formTitle">Select file</p>
            </Col>
            <Col>
              <Form.Group md={6} className="mb-3">
                <Form.Control id="formSelect" onChange={changeHandler} type="file" />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button id="noBorder" onClick={handleClose}>
            Cancel
          </Button>
          <Button id="msg_reply" onClick={submitFile}>
            Attach
          </Button>
        </Modal.Footer>
      </Modal>

      {/* //$ Confirm Message Send */}
      <Modal show={confirmModal} onHide={() => handleClose()} backdrop={"static"}>
        <Modal.Header>
          <Modal.Title>Review recipient info</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div>Please confirm the details of the patient before sending message !</div>
          <br />
          {!selectedPatient ? (
            <div style={{ color: "red" }}>Uh oh... No Patient is currently selected from list</div>
          ) : (
            <>
              <b>
                To: {selectedPatient?.firstName} {selectedPatient?.lastName}
              </b>
              <br />
              <b>Email: {selectedPatient?.email}</b>
              <div>DOB: {selectedPatient?.dob}</div>
              <div>State: {selectedPatient?.state}</div>
            </>
          )}
          <br />
          {selectedPatient && (
            <Col>
              <Form.Group
                as={Row}
                controlId="formBookingId"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: 15
                }}
              >
                <Form.Label style={{ fontSize: 14, textAlign: "left" }}>I have reviewed & ensured the intended receipent is accurate</Form.Label>
                <Col style={{ display: "flex", justifyContent: "flex-end" }}>
                  <Form.Check
                    reverse
                    type="switch"
                    id="custom-switch-confirm"
                    aria-label="confirmation"
                    aria-describedby="basic-addon1"
                    value={verification}
                    onChange={e => setVerification(e.target.checked)}
                  />
                </Col>
              </Form.Group>
            </Col>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Close
          </Button>
          {verification && selectedPatient && (
            <Button
              variant="light"
              className="replyButton sendButton"
              style={{ background: "#866BD9", color: "white" }}
              onClick={sendMessageAPI}
              disabled={msgLoading}
            >
              {msgLoading ? (
                <div>
                  Sending <Spinner animation="border" size="sm" />
                </div>
              ) : (
                "Send Message"
              )}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
      <Snackbar open={snackOpen} autoHideDuration={6000} onClose={handleSnackClose} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
        <Alert onClose={handleSnackClose} severity={snackSeverity}>
          {snackText}
        </Alert>
      </Snackbar>
    </Card>
  );
};
export default SendMessage;
